import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, PageHeader, Row, Col, Input, DatePicker, message } from 'antd';
import DiscountForm from './DiscountForm'; // Adjust the path as needed
import axios from '../backendService'; // Import axios for API requests
import { BiEditAlt } from 'react-icons/bi';
import UilTrashAlt from '@iconscout/react-unicons/icons/uil-trash-alt';
import { saveAs } from 'file-saver';
const { RangePicker } = DatePicker;

const MarketplaceDiscount = () => {
  const [discounts, setDiscounts] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);

  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Fetch discounts
  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get('/discount');
      // console.log(response.data.data, '---------------------------------discounts');
      setDiscounts(response.data.data.filter((item) => item.section === 'MARKETPLACE'));
    } catch (error) {
      console.error('Error fetching discounts:', error);
    }
  };

  // Updated handleDownloadCSV to use date range
  const handleDownloadCSV = () => {
    if (selectedDateRange.length !== 2) {
      setShowDatePicker(true); // Show the date picker if no date range is selected
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    const filteredData = discounts.filter((discount) => {
      const validFrom = new Date(discount.validFrom);
      const validTo = new Date(discount.validTo);
      return validFrom >= startDate && validTo <= endDate;
    });

    const csvData = convertToCSV(filteredData);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'discounts.csv');

    // Reset and hide date picker after download
    setSelectedDateRange([]);
    setShowDatePicker(false);
  };

  // Event handler for RangePicker change
  const handleDateRangeChange = (dates) => {
    if (dates) {
      setSelectedDateRange(dates.map((date) => date.toDate())); // Convert moment objects to Date
    }
  };

  // Call handleDownloadCSV when OK button on RangePicker is clicked
  const onOkRangePicker = () => {
    handleDownloadCSV();
  };

  const convertToCSV = (objArray) => {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';

    // Extract headers
    if (array.length > 0) {
      str += Object.keys(array[0]).join(',') + '\r\n';
    }

    // Extract data
    for (let i = 0; i < array.length; i++) {
      let line = '';
      for (let index in array[i]) {
        if (line !== '') line += ',';

        line += array[i][index];
      }

      str += line + '\r\n';
    }

    return str;
  };

  // Add or Update discount
  const handleFormSubmit = async (values) => {
    const data = {
      flatAmount: values.flatAmount,
      maxAmount: values.maxAmount,
      percent: values.percent,
      redemptionLimit: values.redemptionLimit,
      title: values.title,
      validFrom: values.validFrom,
      validTo: values.validTo,
      section: 'MARKETPLACE',
    };
    try {
      if (editingDiscount) {
        const response = await axios.put(`/discount/${editingDiscount._id}`, data);
        if (response.data.status_code === 200) {
          message.success(response.data.message);
          console.log(response.data, '----------------------------edit res');
          setIsFormVisible(false);
          setEditingDiscount(null);
          fetchDiscounts();
        } else if (response.data.status_code === 400) {
          message.success(response.data.message);
          console.error(response.data.message);
        }
      } else {
        const response = await axios.post('/discount', data);
        if (response.data.status_code === 200) {
          message.success(response.data.message);
          console.log(response.data, '----------------------------create res');
          setIsFormVisible(false);
          setEditingDiscount(null);
          fetchDiscounts();
        } else if (response.data.status_code === 400) {
          message.success(response.data.message);
          console.error(response.data.message);
        }
      }
    } catch (error) {
      console.error('Error updating/adding discount:', error);
    }
  };

  const openFormForAdd = () => {
    setEditingDiscount(null); // Reset editing discount
    setIsFormVisible(true);
  };

  // Edit discount
  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setIsFormVisible(true);
  };

  // Delete discount
  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this discount?',
      onOk: async () => {
        try {
          await axios.delete(`/discount/${id}`);
          fetchDiscounts();
        } catch (error) {
          console.error('Error deleting discount:', error);
        }
      },
    });
  };

  // Columns for table
  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Coupon Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Valid From',
      dataIndex: 'validFrom',
      key: 'validFrom',
      render: (text) => text && new Date(text).toLocaleDateString(),
    },
    {
      title: 'Valid To',
      dataIndex: 'validTo',
      key: 'validTo',
      render: (text) => text && new Date(text).toLocaleDateString(),
    },
    {
      title: 'Percentage Discount',
      dataIndex: 'percent',
      key: 'percent',
      render: (text) => `${text}%`,
    },
    {
      title: 'Max Amount',
      dataIndex: 'maxAmount',
      key: 'maxAmount',
      render: (text) => `Rs. ${text}`,
    },
    {
      title: 'Flat Discount',
      dataIndex: 'flatAmount',
      key: 'flatAmount',
      render: (text) => (text ? `Rs. ${text}` : '—'), // Display '—' if there's no flat discount
    },
    {
      title: 'Redemption Limit',
      dataIndex: 'redemptionLimit',
      key: 'redemptionLimit',
      align: 'center',
    },
    {
      title: 'Redemptions Used',
      dataIndex: 'redemptionCount',
      key: 'redemptionCount',
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div className="flex">
          <BiEditAlt
            className="mx-2 w-4 h-4 cursor-pointer text-light dark:text-white60 hover:text-success dark:hover:text-success"
            onClick={() => handleEdit(record)}
          />
          <UilTrashAlt
            className="mx-2 w-4 h-4 cursor-pointer text-light dark:text-white60 hover:text-danger dark:hover:text-danger"
            onClick={() => handleDelete(record._id)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        className="flex items-center justify-between px-8 xl:px-[15px] pt-7 pb-6 sm:pb-[30px] bg-transparent sm:flex-col"
        title="  "
      />
      <main className="min-h-[715px] lg:min-h-[580px] px-8 xl:px-[15px] bg-transparent">
        <Row gutter={30}>
          <Col xs={24}>
            <>
              <div className="bg-white dark:bg-white10 m-0 text-theme-gray dark:text-white60 text-[15px] rounded-10 relative p-[25px]">
                <div className="text-dark dark:text-white87 font-medium text-[17px] flex items-center justify-between gap-[15px] sm:justify-left mb-5">
                  <div className="flex items-center justify-between gap-[15px]">
                    <h1 className="mb-0 inline-block text-ellipsis text-[18px] font-semibold sm:text-center">
                      Discount
                    </h1>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      // onClick={() => handleEdit(null)}
                      onClick={openFormForAdd}
                      className="h-[50px] px-6 text-xs font-medium bg-primary-transparent text-primary border-none"
                      transparented
                      type="primary"
                      size="large"
                    >
                      + Add Discount
                    </Button>

                    <Button
                      size="large"
                      type="primary"
                      className="h-[50px] px-6 text-xs font-medium bg-primary-transparent text-primary border-none"
                      transparented
                      onClick={() => setShowDatePicker(true)}
                    >
                      Download CSV
                    </Button>
                    <Modal
                      title="Select Date Range for CSV"
                      visible={showDatePicker}
                      onOk={onOkRangePicker}
                      onCancel={() => setShowDatePicker(false)}
                      okText="Download"
                    >
                      <RangePicker onChange={handleDateRangeChange} />
                    </Modal>
                  </div>
                </div>
                {/* Display discounts list */}
                <div className="mt-0.5">
                  <>
                    <Table
                      dataSource={discounts.sort((a, b) => b.id - a.id)}
                      columns={columns}
                      rowKey={(record) => record._id}
                      pagination={{
                        defaultPageSize: 7,
                        total: discounts.length,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        className:
                          '[&>.ant-pagination-total-text]:border-none ssm:[&>.ant-pagination-total-text]:w-full [&>.ant-pagination-total-text]:text-center [&>.ant-pagination-total-text]:text-dark dark:[&>.ant-pagination-total-text]:text-white87 [&>.ant-pagination-total-text]:font-medium [&>.ant-pagination-total-text]:text-[17px md:justify-center',
                      }}
                    />
                  </>
                </div>
              </div>
            </>
          </Col>
        </Row>
      </main>
      {/* Render DiscountForm here */}
      <DiscountForm
        key={editingDiscount ? editingDiscount._id : 'new'} // Use discount ID or 'new' for adding
        visible={isFormVisible}
        onCancel={() => setIsFormVisible(false)}
        onSubmit={handleFormSubmit}
        initialData={editingDiscount}
      />
    </>
  );
};

export default MarketplaceDiscount;
